/* Deriving a lesson's sections and its acts from the content.
 *
 * Both are computed from the block list rather than collected while rendering,
 * so the nav and the page agree on the same list in the same order without any
 * render-order coupling.
 */

const slug = s => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 48);

/* Only beats the student can actually see. `outcomes` and `anticipated` are
   instructor-only (see INSTRUCTOR_ONLY in LessonBody) — offering a jump to a
   section that does not render would scroll to nothing. */
const BEAT_LABEL = {
  hook: 'Try this first',
  recall: 'Before you start',
  compare: 'The comparison',
  revisit: 'Back to the opening',
  apply: 'Apply it',
  summary: 'Remember',
};

/* A procedural lesson has several steps, so a step's anchor carries its
   number; every other beat occurs once. `n` is assigned by content/build.mjs. */
const beatId = (beat, n) =>
  beat === 'step' ? `s-step-${n}` : BEAT_LABEL[beat] ? `s-${beat}` : undefined;

/** The jump list: every beat and every subheading, in document order. */
export function sectionsOf(body) {
  const out = [];
  for (const b of body || []) {
    if (b.beat === 'step') out.push({ id: beatId('step', b.n), label: b.label ? `${b.n} · ${b.label}` : `Step ${b.n}`, kind: 'beat' });
    else if (b.beat && BEAT_LABEL[b.beat]) out.push({ id: beatId(b.beat), label: BEAT_LABEL[b.beat], kind: 'beat' });
    else if (b.h) out.push({ id: `s-${slug(b.h)}`, label: b.h, kind: 'heading' });
    else if (b.scaffold?.label) out.push({ id: `s-${slug(b.scaffold.label)}`, label: b.scaffold.label, kind: 'heading' });
  }
  return out;
}

/* Acts — the full-bleed bands.
 *
 * A band is a value change across the whole width, so it has to mark something
 * worth marking: 01-explanations rule 9, don't highlight everything. Long-form
 * reading stays on paper; the inverted bands carry short, high-signal content
 * only (the comparison table, the summary).
 */
const TONE = {
  hook: 'open',
  recall: 'open',
  compare: 'invert',
  summary: 'invert',
};

/* Instructor-only beats are dropped before banding, not merely left untoned:
   a block that renders nothing must not be able to open a band, or a lesson
   gets an empty stripe of colour where the outcomes list used to be. */
const HIDDEN = new Set(['outcomes', 'anticipated']);

/** Group consecutive blocks that share a tone into one band. */
export function actsOf(body) {
  const acts = [];
  for (const b of (body || []).filter(x => !(x.beat && HIDDEN.has(x.beat)))) {
    const tone = (b.beat && TONE[b.beat]) || 'read';
    const last = acts[acts.length - 1];
    if (last && last.tone === tone) last.blocks.push(b);
    else acts.push({ tone, blocks: [b] });
  }
  return acts;
}

export { slug, BEAT_LABEL, beatId };
